/**
 * StylePresets.ts - Pre-built style library
 * 
 * CHANGE HISTORY:
 * - 251102_1715 danielbeer: Created for Phase 3.5b - Advanced Features
 * 
 * Provides ready-to-use style presets for common UI elements
 */

import type { CreateStyleParams } from '../types';
import { CSSHelpers } from './CSSHelpers';

export interface StylePreset {
  name: string;
  description: string;
  type: string;
  variables: Record<string, string | number>;
  category: string;
}

/**
 * Button presets
 */
export const BUTTON_PRESETS: Record<string, StylePreset> = {
  modern: {
    name: 'Modern Button',
    description: 'Clean, modern button with subtle shadow',
    type: 'button',
    category: 'buttons',
    variables: {
      '--padding': '12px 24px',
      '--radius': '8px',
      '--bg': '#3b82f6',
      '--text': '#ffffff',
      '--font-weight': '600',
      '--border': 'none',
      '--box-shadow': '0 2px 4px rgba(0,0,0,0.1)',
      '--transition': 'all 200ms ease',
      ...CSSHelpers.flex.createFlexCenter()
    }
  },
  
  outlined: {
    name: 'Outlined Button',
    description: 'Button with border, no background',
    type: 'button',
    category: 'buttons',
    variables: {
      '--padding': '12px 24px',
      '--radius': '8px',
      '--bg': 'transparent',
      '--text': '#3b82f6',
      '--font-weight': '600',
      '--border': '2px solid #3b82f6',
      '--transition': 'all 200ms ease'
    }
  },
  
  ghost: {
    name: 'Ghost Button',
    description: 'Minimal button with hover effect',
    type: 'button',
    category: 'buttons',
    variables: {
      '--padding': '12px 24px',
      '--radius': '8px',
      '--bg': 'transparent',
      '--text': '#3b82f6',
      '--font-weight': '600',
      '--border': 'none',
      '--transition': 'background 200ms ease'
    }
  }
};

/**
 * Card presets
 */
export const CARD_PRESETS: Record<string, StylePreset> = {
  elevated: {
    name: 'Elevated Card',
    description: 'Card with shadow elevation',
    type: 'card',
    category: 'cards',
    variables: {
      '--padding': '24px',
      '--radius': '12px',
      '--bg': '#ffffff',
      '--border': 'none',
      ...CSSHelpers.shadow.elevation(2)
    }
  },
  
  bordered: {
    name: 'Bordered Card',
    description: 'Card with border, no shadow',
    type: 'card',
    category: 'cards',
    variables: {
      '--padding': '24px',
      '--radius': '12px',
      '--bg': '#ffffff',
      '--border': '1px solid #e5e7eb'
    }
  },
  
  glass: {
    name: 'Glass Card',
    description: 'Glassmorphism effect card',
    type: 'card',
    category: 'cards',
    variables: {
      '--padding': '24px',
      '--radius': '16px',
      '--bg': 'rgba(255, 255, 255, 0.7)',
      '--backdrop-filter': 'blur(10px)',
      '--border': '1px solid rgba(255, 255, 255, 0.3)',
      '--box-shadow': '0 8px 32px rgba(0, 0, 0, 0.1)'
    }
  }
};

/**
 * Input presets
 */
export const INPUT_PRESETS: Record<string, StylePreset> = {
  modern: {
    name: 'Modern Input',
    description: 'Clean input with focus ring',
    type: 'input',
    category: 'inputs',
    variables: {
      '--padding': '12px 16px',
      '--radius': '8px',
      '--bg': '#ffffff',
      '--text': '#1f2937',
      '--border': '1px solid #d1d5db',
      '--font-size': '14px',
      '--transition': 'border-color 200ms ease'
    }
  },
  
  filled: {
    name: 'Filled Input',
    description: 'Input with filled background',
    type: 'input',
    category: 'inputs',
    variables: {
      '--padding': '12px 16px',
      '--radius': '8px',
      '--bg': '#f3f4f6',
      '--text': '#1f2937',
      '--border': 'none',
      '--font-size': '14px'
    }
  },
  
  underlined: {
    name: 'Underlined Input',
    description: 'Minimal underlined input',
    type: 'input',
    category: 'inputs',
    variables: {
      '--padding': '8px 0',
      '--radius': '0',
      '--bg': 'transparent',
      '--text': '#1f2937',
      '--border': 'none',
      '--border-bottom': '2px solid #d1d5db',
      '--font-size': '14px'
    }
  }
};

/**
 * Layout presets
 */
export const LAYOUT_PRESETS: Record<string, StylePreset> = {
  flexRow: {
    name: 'Flex Row',
    description: 'Horizontal flex container',
    type: 'container',
    category: 'layouts',
    variables: CSSHelpers.flex.createFlex('row', '16px') as Record<string, string | number>
  },
  
  flexColumn: {
    name: 'Flex Column',
    description: 'Vertical flex container',
    type: 'container',
    category: 'layouts',
    variables: CSSHelpers.flex.createFlex('column', '16px') as Record<string, string | number>
  },
  
  gridResponsive: {
    name: 'Responsive Grid',
    description: 'Auto-fit responsive grid',
    type: 'container',
    category: 'layouts',
    variables: CSSHelpers.grid.createResponsiveGrid('250px', '24px') as Record<string, string | number>
  },
  
  centered: {
    name: 'Centered Container',
    description: 'Centered flex container',
    type: 'container',
    category: 'layouts',
    variables: CSSHelpers.flex.createFlexCenter() as Record<string, string | number>
  }
};

/**
 * Navigation presets
 */
export const NAVIGATION_PRESETS: Record<string, StylePreset> = {
  navbar: {
    name: 'Horizontal Navbar',
    description: 'Horizontal navigation bar',
    type: 'navigation',
    category: 'navigation',
    variables: {
      '--padding': '16px 24px',
      '--bg': '#ffffff',
      '--border-bottom': '1px solid #e5e7eb',
      ...CSSHelpers.flex.createFlexSpaceBetween('row') as Record<string, string | number>,
      ...CSSHelpers.shadow.elevation(1) as Record<string, string | number>
    }
  },
  
  sidebar: {
    name: 'Vertical Sidebar',
    description: 'Vertical sidebar navigation',
    type: 'navigation',
    category: 'navigation',
    variables: {
      '--padding': '24px 16px',
      '--bg': '#f9fafb',
      '--border-right': '1px solid #e5e7eb',
      '--width': '250px',
      '--min-height': '100vh',
      ...CSSHelpers.flex.createFlex('column', '8px') as Record<string, string | number>
    }
  },
  
  breadcrumbs: {
    name: 'Breadcrumbs',
    description: 'Breadcrumb navigation',
    type: 'navigation',
    category: 'navigation',
    variables: {
      '--padding': '8px 0',
      '--font-size': '14px',
      '--text': '#6b7280',
      ...CSSHelpers.flex.createFlex('row', '8px') as Record<string, string | number>
    }
  },
  
  tabs: {
    name: 'Tabs',
    description: 'Tab navigation',
    type: 'navigation',
    category: 'navigation',
    variables: {
      '--padding': '12px 24px',
      '--border-bottom': '2px solid transparent',
      '--text': '#6b7280',
      '--transition': 'all 200ms ease',
      ...CSSHelpers.flex.createFlex('row', '0') as Record<string, string | number>
    }
  },
  
  pagination: {
    name: 'Pagination',
    description: 'Pagination controls',
    type: 'navigation',
    category: 'navigation',
    variables: {
      '--padding': '8px 12px',
      '--radius': '6px',
      '--bg': '#ffffff',
      '--border': '1px solid #e5e7eb',
      '--text': '#374151',
      ...CSSHelpers.flex.createFlex('row', '4px') as Record<string, string | number>
    }
  }
};

/**
 * Modal/Dialog presets
 */
export const MODAL_PRESETS: Record<string, StylePreset> = {
  modal: {
    name: 'Standard Modal',
    description: 'Standard modal dialog',
    type: 'modal',
    category: 'modals',
    variables: {
      '--padding': '24px',
      '--radius': '12px',
      '--bg': '#ffffff',
      '--max-width': '500px',
      '--width': '90%',
      ...CSSHelpers.shadow.elevation(5) as Record<string, string | number>
    }
  },
  
  alert: {
    name: 'Alert Dialog',
    description: 'Alert/warning dialog',
    type: 'modal',
    category: 'modals',
    variables: {
      '--padding': '20px',
      '--radius': '8px',
      '--bg': '#ffffff',
      '--max-width': '400px',
      '--border-left': '4px solid #ef4444',
      ...CSSHelpers.shadow.elevation(3) as Record<string, string | number>
    }
  },
  
  confirmation: {
    name: 'Confirmation Dialog',
    description: 'Confirmation dialog',
    type: 'modal',
    category: 'modals',
    variables: {
      '--padding': '24px',
      '--radius': '10px',
      '--bg': '#ffffff',
      '--max-width': '450px',
      '--text-align': 'center',
      ...CSSHelpers.shadow.elevation(4) as Record<string, string | number>
    }
  }
};

/**
 * Tooltip/Popover presets
 */
export const TOOLTIP_PRESETS: Record<string, StylePreset> = {
  tooltip: {
    name: 'Tooltip',
    description: 'Tooltip popup',
    type: 'tooltip',
    category: 'tooltips',
    variables: {
      '--padding': '6px 12px',
      '--radius': '6px',
      '--bg': '#1f2937',
      '--text': '#ffffff',
      '--font-size': '12px',
      '--max-width': '200px',
      ...CSSHelpers.shadow.elevation(2) as Record<string, string | number>
    }
  },
  
  popover: {
    name: 'Popover',
    description: 'Popover panel',
    type: 'popover',
    category: 'tooltips',
    variables: {
      '--padding': '16px',
      '--radius': '8px',
      '--bg': '#ffffff',
      '--border': '1px solid #e5e7eb',
      '--max-width': '300px',
      ...CSSHelpers.shadow.elevation(3) as Record<string, string | number>
    }
  },
  
  dropdown: {
    name: 'Dropdown Menu',
    description: 'Dropdown menu',
    type: 'dropdown',
    category: 'tooltips',
    variables: {
      '--padding': '8px 0',
      '--radius': '8px',
      '--bg': '#ffffff',
      '--border': '1px solid #e5e7eb',
      '--min-width': '200px',
      ...CSSHelpers.shadow.elevation(2) as Record<string, string | number>
    }
  }
};

/**
 * Badge/Chip presets
 */
export const BADGE_PRESETS: Record<string, StylePreset> = {
  badge: {
    name: 'Badge',
    description: 'Status badge',
    type: 'badge',
    category: 'badges',
    variables: {
      '--padding': '2px 8px',
      '--radius': '12px',
      '--bg': '#3b82f6',
      '--text': '#ffffff',
      '--font-size': '12px',
      '--font-weight': '600',
      ...CSSHelpers.flex.createFlexCenter() as Record<string, string | number>
    }
  },
  
  chip: {
    name: 'Chip',
    description: 'Chip/tag element',
    type: 'chip',
    category: 'badges',
    variables: {
      '--padding': '6px 12px',
      '--radius': '16px',
      '--bg': '#f3f4f6',
      '--text': '#374151',
      '--font-size': '14px',
      '--border': '1px solid #e5e7eb',
      ...CSSHelpers.flex.createFlex('row', '6px') as Record<string, string | number>
    }
  }
};

/**
 * Progress presets
 */
export const PROGRESS_PRESETS: Record<string, StylePreset> = {
  progressBar: {
    name: 'Progress Bar',
    description: 'Linear progress bar',
    type: 'progress',
    category: 'progress',
    variables: {
      '--height': '8px',
      '--radius': '4px',
      '--bg': '#e5e7eb',
      '--fill-bg': '#3b82f6',
      '--width': '100%',
      '--transition': 'width 300ms ease'
    }
  },
  
  circularProgress: {
    name: 'Circular Progress',
    description: 'Circular progress indicator',
    type: 'progress',
    category: 'progress',
    variables: {
      '--size': '40px',
      '--border-width': '4px',
      '--border-color': '#e5e7eb',
      '--fill-color': '#3b82f6',
      '--border-radius': '50%'
    }
  },
  
  skeleton: {
    name: 'Skeleton Loader',
    description: 'Skeleton loading placeholder',
    type: 'skeleton',
    category: 'progress',
    variables: {
      '--height': '20px',
      '--radius': '4px',
      '--bg': '#e5e7eb',
      '--animation': 'pulse 1.5s ease-in-out infinite'
    }
  }
};

/**
 * All presets combined
 */
export const STYLE_PRESETS = {
  buttons: BUTTON_PRESETS,
  cards: CARD_PRESETS,
  inputs: INPUT_PRESETS,
  layouts: LAYOUT_PRESETS,
  navigation: NAVIGATION_PRESETS,
  modals: MODAL_PRESETS,
  tooltips: TOOLTIP_PRESETS,
  badges: BADGE_PRESETS,
  progress: PROGRESS_PRESETS
};

/**
 * Get all preset categories
 */
export function getPresetCategories(): string[] {
  return Object.keys(STYLE_PRESETS);
}

/**
 * Get presets by category
 */
export function getPresetsByCategory(category: keyof typeof STYLE_PRESETS): StylePreset[] {
  return Object.values(STYLE_PRESETS[category]);
}

/**
 * Get all presets
 */
export function getAllPresets(): StylePreset[] {
  return Object.values(STYLE_PRESETS).flatMap(category => Object.values(category));
}

/**
 * Convert preset to CreateStyleParams
 */
export function presetToStyleParams(preset: StylePreset): CreateStyleParams {
  return {
    name: preset.name,
    type: preset.type,
    variables: preset.variables as any,
    metadata: {
      preset: true,
      category: preset.category,
      description: preset.description
    }
  };
}
